import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useMemo } from 'react';
import type { GroupBy } from '../../../components/work-items/types';
import { columnDroppableId, epicGroupKey } from './boardDnd';
import { BoardCard } from './BoardCard';
import { BoardEpicGroup } from './BoardEpicGroup';
import type { BoardWorkItemRow } from './types';

export function BoardColumn({
  stage,
  cards,
  groupBy,
  visibleChipKeys,
  parentNameById,
  stages,
  onOpen,
  onParentOpen,
  onChangeStatus
}: {
  stage: string;
  cards: ReadonlyArray<BoardWorkItemRow>;
  groupBy: GroupBy;
  visibleChipKeys: ReadonlyArray<string>;
  parentNameById: Map<string, string>;
  stages: ReadonlyArray<string>;
  onOpen: (card: BoardWorkItemRow) => void;
  onParentOpen: (parentId: string) => void;
  onChangeStatus: (cardId: string, nextStatus: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnDroppableId(stage) });
  const cardIds = useMemo(() => cards.map((c) => c.id), [cards]);

  const grouped = useMemo(() => {
    if (groupBy !== 'epic') return null;
    const buckets = new Map<string, BoardWorkItemRow[]>();
    for (const c of cards) {
      const key = epicGroupKey(c.parent_work_item_id);
      const arr = buckets.get(key);
      if (arr) arr.push(c);
      else buckets.set(key, [c]);
    }
    const order = Array.from(buckets.keys()).sort((a, b) => {
      const an = a === '~unassigned' ? '￿' : parentNameById.get(a) ?? a;
      const bn = b === '~unassigned' ? '￿' : parentNameById.get(b) ?? b;
      return an.localeCompare(bn);
    });
    return order.map((key) => ({
      key,
      epicId: key === '~unassigned' ? null : key,
      epicName:
        key === '~unassigned' ? 'No epic' : parentNameById.get(key) ?? 'Epic',
      cards: (buckets.get(key) ?? []).sort((a, b) => a.rank - b.rank)
    }));
  }, [cards, groupBy, parentNameById]);

  return (
    <div
      ref={setNodeRef}
      className="work-items-route__board-col"
      data-testid={`work-items-route-board-col-${stage}`}
      data-stage={stage}
      data-over={isOver}
      data-card-count={cards.length}
    >
      <header className="work-items-route__board-col-head">
        <span className="work-items-route__board-col-name">{stage}</span>
        <span className="work-items-route__board-col-count">{cards.length}</span>
      </header>
      <div className="work-items-route__board-col-body">
        {grouped
          ? grouped.map((group) => (
              <BoardEpicGroup
                key={group.key}
                stage={stage}
                groupKey={group.key}
                epicId={group.epicId}
                epicName={group.epicName}
                cards={group.cards}
                visibleChipKeys={visibleChipKeys}
                parentNameById={parentNameById}
                stages={stages}
                onOpen={onOpen}
                onParentOpen={onParentOpen}
                onChangeStatus={onChangeStatus}
              />
            ))
          : (
            <SortableContext
              items={cardIds}
              strategy={verticalListSortingStrategy}
            >
              {cards.map((c) => (
                <BoardCard
                  key={c.id}
                  card={c}
                  visibleChipKeys={visibleChipKeys}
                  parentName={
                    c.parent_work_item_id
                      ? parentNameById.get(c.parent_work_item_id) ?? null
                      : null
                  }
                  stages={stages}
                  onOpen={() => onOpen(c)}
                  onParentOpen={onParentOpen}
                  onChangeStatus={onChangeStatus}
                />
              ))}
            </SortableContext>
          )}
      </div>
    </div>
  );
}
